// Types for the workflow-connection store at organigram/{organigram_id}/organigram_workflow_trial/{connId}.
//
// (This file once held helpers for a Firestore "mirror" of every trigger node; that mirror was removed
// 2026-08-19 as redundant - a trigger is an ordinary backend node. All that remains is the connection
// type below.)

// A FOR-EACH repeat on a connection: the target step runs ONCE PER ENTRY of `items`. Written into the
// edge doc as-is, so the key names are the backend's, not camelCase. ORTHOGONAL to `if`/`else`: the gate
// is evaluated first and a pruned arrow never fans out, so an edge legitimately carries `if` AND this.
// THREE keys, and only `items` is required: the entry is ALWAYS bound as `item`, what later steps
// receive is always {total, done, failed, truncated}, and the window and the ceiling belong to the
// engine.
export interface ForEachSpec {
  // REQUIRED. A JSON-logic rule ({ var: 'output.emails' }) read against the same context an `if` gets.
  // It MUST resolve to an Array - a non-array fails the deciding step LOUDLY at run time, never a
  // silent skip. NEVER a "{{ }}" template: templating stringifies the array the loop has to walk, so a
  // template here would be a silent wrong answer. The list has no authorable cap: the engine stops at
  // 5000 entries and says so through the run's `truncated`.
  items: any;
  // OPTIONAL per-entry filter: which entries of `items` actually run. A JSON-logic rule evaluated ONCE
  // PER ENTRY, with `item` (the entry), `index` (its zero-based position) and the same run context an
  // `if` gets - so an entry can be compared against the previous step's answer and not only a literal.
  //
  // The AXIS this sits on is what tells it apart from the gate: `if` on the edge turns the WHOLE repeat
  // on or off, `where` picks which entries inside it run. Both can be on one arrow.
  //
  // An excluded entry is NEVER CREATED: it gets no workflow_iterations document, so `total` is the
  // number that actually runs and a progress counter built on it describes real work. The number
  // dropped comes back on the run as `filtered`. FAIL-CLOSED per entry: one whose rule cannot be
  // evaluated is dropped, not run.
  //
  // `where` WITHOUT `items` is not a repeat - a filter with no list has nothing to filter - so the
  // reader drops the whole map (see isLoopEdge, and the `items` guards in OrganigramApiService).
  where?: any;
  // OPTIONAL per-entry prompt, with the entry bound as {{ item }} / {{ item.<field> }}. OMIT IT and the
  // entry itself is what the step receives, with the step's own instruction saying what to do with it -
  // which is what the editor always does. Read and preserved so a hand-written one is never destroyed.
  message?: string;
  // OPTIONAL: the ids of the LAST nodes inside the repeat, making the body a RUN OF STEPS rather than
  // the single target. ABSENT means the target is the whole body, which is every repeat authored before
  // this key existed - so there is nothing to migrate and no "one node or several" mode to store.
  //
  // A LIST because a body can end in more than one place. A chain has one last step; a body that takes
  // in an if/else fork has one last step PER ARM, and no single id can name them - naming either arm
  // would leave the other half of the same decision outside the loop. One entry is a chain, several are
  // a fork's arms.
  //
  // The body itself is NOT stored. The engine derives it at plan time by walking the live graph from
  // `target` to these ends (see loop-body.ts, which runs the identical walk to fill the picker), so the
  // graph is the only truth between runs and the derived list is frozen on the anchor once a run
  // starts. Ends the walk cannot reach are refused LOUDLY at plan time - never a silent shrink to one
  // step, and never a silent grow to the rest of the workflow.
  body_ends?: string[];
  // Read, never written: the singular key this was called before a body could end in more than one
  // place. bodyEndsOf() accepts it so a doc from that shape still opens.
  body_end?: string;
}

// A REPEAT-UNTIL repeat on a connection: the target step, and the run of steps after it, run AGAIN
// until `until` passes - up to a capped number of attempts. The other answer to "how often does this
// arrow run", and never the same arrow as `for_each`: once per entry and until a rule passes have no
// combined meaning, and the engine throws on an edge carrying both (planAll, foreach.js).
//
// `until` PRESENCE is the repeat, exactly as `items` presence is a for_each - so a started-but-rule-less
// map is NO repeat, and a reader that keys on the key's existence would draw one that cannot run.
export interface RepeatUntilSpec {
  // REQUIRED. A JSON-logic rule judged against the answer of the node where the attempt ENDED, in a
  // context of exactly three names: `output` (that answer), `attempt` (0-based) and `total` (the cap).
  // A path rooted anywhere else - `steps.` above all - is well-formed, resolves to null on every
  // attempt, and so spends the whole budget before giving up. NEVER a "{{ }}" template: the engine
  // evaluates a rule and templates a string, so a template here passes its own first test and the loop
  // exits after one pass. See until-rule.ts, which mirrors the engine's own two checks.
  until: any;
  // OPTIONAL cap on the passes. The engine CLAMPS it and an author may only go LOWER, because the
  // budget is counted in AGENT CALLS: a longer body buys proportionally fewer attempts. Junk (true,
  // [3], "0x3") is NOT a number and silently lands on the ceiling, which is why the editor validates it
  // rather than coercing. A plain decimal string is accepted - an edge that round-trips through a form
  // arrives carrying "3", not 3.
  max_attempts?: number;
  // OPTIONAL. What running out of attempts means: "fail" (the default) fails the step, "continue"
  // carries on with the last rejected answer, flagged `gave_up` for whatever reads it next. Any other
  // value silently becomes "fail", so the editor offers exactly the two.
  on_give_up?: 'fail' | 'continue';
  // OPTIONAL, and the SAME key `for_each` writes - the body picker serves both kinds without knowing
  // which it is filling. Absent means the body is the target alone. See ForEachSpec.body_ends.
  body_ends?: string[];
  // Read, never written: the singular spelling, tolerated for a doc written against the first cut.
  body_end?: string;
  // OPTIONAL per-attempt prompt. Read and preserved so a hand-written one is never destroyed by a save
  // from a pane that offers no control for it - the same trap ForEachSpec.message exists to plug.
  message?: string;
  // OPTIONAL. Whose answer the loop hands on: absent (the engine's "checked_work") is the work the last
  // step judged, "last_answer" the last step's own reply. No control authors it; read and preserved.
  hand_on?: string;
}

// The `for_each` map off a raw connection doc, or null when there is none to read. A map without
// `items` is NOT a repeat (the list IS the repeat), so it reads null rather than being handed on
// half-written. ONE predicate because four write paths and both copy paths ask the same question, and
// four inline copies of it are four chances to drift.
export function forEachOf(raw: any): ForEachSpec | null {
  const spec = raw && raw.for_each;
  if (!spec || typeof spec !== 'object' || Array.isArray(spec)) { return null; }
  return spec.items !== undefined && spec.items !== null ? spec as ForEachSpec : null;
}

// The `repeat_until` map off a raw connection doc, or null. `until` is to this what `items` is to a
// for_each: the key that makes the map a repeat at all.
export function repeatUntilOf(raw: any): RepeatUntilSpec | null {
  const spec = raw && raw.repeat_until;
  if (!spec || typeof spec !== 'object' || Array.isArray(spec)) { return null; }
  return spec.until !== undefined && spec.until !== null ? spec as RepeatUntilSpec : null;
}

// A workflow CONNECTION: the nodes themselves are real backend nodes (children of the trigger, so
// start_message can run them); this doc is only the FRONTEND link between two of them - `source` flows to
// `target`. It is NOT a backend edge, so wiring a connection never reparents in the backend and never
// changes the backend fan-out. The canvas draws it as an arrow on top of the nodes.
export interface WorkflowConnection {
  id: string;
  source: string;   // the node the flow comes FROM - a step, or the trigger for the first step
  target: string;   // the node the flow goes TO
  // The branch condition the trigger runner reads off this edge. A plain arrow carries neither.
  // `if` is a JSON-logic rule (this arrow is followed when it evaluates true); `else` is true on the
  // fallback arrow (followed when no sibling `if` from the same source matched). Never both. See
  // workflow-condition.ts for how the editor builds and reads these.
  if?: any;
  else?: boolean;
  // The repeat. Followed ALWAYS, once per entry, and it COMPOSES with `if` (gate first, then fan out) -
  // which is why it is a separate key and not a third value of the if/else pair. Read and written on its
  // own path: a gate edit never names this key, and a repeat edit never names if/else. It never
  // accompanies `else` - that shape is undefined by the backend, and workflow-loop.ts refuses it.
  for_each?: ForEachSpec;
  // The OTHER repeat, and never beside the one above: the two are different answers to "how often",
  // and the engine throws on an edge carrying both rather than letting whichever planner ran first win.
  // It composes with `if` the same way, for the same reason.
  repeat_until?: RepeatUntilSpec;
}

// A patch on ONE connection's routing. Only the keys PRESENT are touched; an omitted key is left exactly
// as it is, and `null` clears. That opt-in shape is what lets the gate and the repeat be edited
// independently: an arrow can carry BOTH, so a gate edit must never wipe the repeat and a repeat edit
// must never wipe the gate.
export interface ConnectionPatch {
  if?: any | null;
  else?: boolean | null;
  for_each?: ForEachSpec | null;
  // Naming this key with a map CLEARS `for_each` in the same write, and naming that one clears this -
  // mutual exclusivity enforced where one atomic update sees both keys, not only in the editor that
  // happens to be open. See updateWorkflowConnection.
  repeat_until?: RepeatUntilSpec | null;
}

// Follow the recorded flow from the trigger to its current last step - the node a new step attaches
// to (the trigger itself while it has no steps). One successor per source, so on a branched workflow
// the arrow listed last is the one followed; a cycle in the docs stops the walk rather than hanging
// it. GraphModelService.workflowChainTail (the canvas) and OrganigramApiService.appendWorkflowStep
// (the pipeline wizard, which cannot see the canvas model) are both this walk, so a step added from
// either lands on the same tail.
export function chainTail(connections: { source: string; target: string }[], triggerId: string): string {
  const next = new Map<string, string>();
  for (const c of connections) { next.set(c.source, c.target); }
  let at = triggerId;
  const seen = new Set<string>([at]);
  while (next.has(at)) {
    const to = next.get(at) as string;
    if (seen.has(to)) { break; }
    seen.add(to);
    at = to;
  }
  return at;
}
